import React, {memo, useContext, useRef} from 'react';
import {FormattedMessage} from 'react-intl';

import isEqual from '@tinkoff/utils/is/equal';

import {useIntersectionObserver} from 'usehooks-ts';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';
import type {GlobalState} from 'types/store';

import type {Post} from 'mattermost-redux/types/posts';

import {selectPost} from 'actions/views/rhs';

import {getPost} from 'mattermost-redux/selectors/entities/posts';

import PostContext from 'components/post_view/post_context';
import Avatars from 'components/widgets/users/avatars';
import Timestamp from 'components/timestamp';
import SimpleTooltip from 'components/widgets/simple_tooltip';
import Button from 'components/threading/common/button';

import {THREADING_TIME} from 'components/threading/common/options';
import {trackEvent} from 'actions/telemetry_actions';
import {selectThreadOrSyntheticThread} from 'features/threads/selectors/select_thread_or_synthetic_thread';

import {getThreadPostFollowState} from 'features/threads/selectors/get_thread_post_follow_state';
import {selectThreadById} from 'features/threads/selectors/select_thread_by_id';

import {getThreadReplyCount} from 'features/threads/selectors/get_thread_reply_count';

import type {ClientThread} from 'features/threads/types/extended';

import {markThreadLinkClicked} from '@time-webkit/performance';

import {useFetchThread} from './use_fetch_thread';

import './thread_footer.scss';

type Props = {
    threadId: ClientThread['id'];
    replyClick?: React.EventHandler<React.MouseEvent>;
};

const NewReplies: React.FC<{newReplies: number}> = (props) => {
    return (
        <SimpleTooltip
            id='threadFooterIndicator'
            content={
                <FormattedMessage
                    id='threading.numNewMessages'
                    defaultMessage='{newReplies, plural, =0 {no unread messages} =1 {one unread message} other {# unread messages}}'
                    values={{newReplies: props.newReplies}}
                />
            }
        >
            <div
                className='indicator'
                tabIndex={0}
            >
                <div className='dot-unreads' />
            </div>
        </SimpleTooltip>
    );
};

const LastReplyButton: React.FC<{
    onClick: (...args: any[]) => void;
    userIds: string[];
    totalReplies: number;
    lastReplyAt: number;
}> = (props) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const {lastReplyAt, onClick, totalReplies, userIds} = props;

    const entry = useIntersectionObserver(buttonRef, {
        freezeOnceVisible: true,
    });

    const shouldFetch = entry?.isIntersecting;

    return (
        <Button
            ref={buttonRef}
            onClick={onClick}
            className='ReplyButton separated'
        >
            {userIds ? (
                <Avatars
                    userIds={userIds}
                    size='sm'
                    fetchMissingUsers={shouldFetch}
                />
            ) : null}

            <span className='replies-count'>
                <FormattedMessage
                    id='threading.numReplies'
                    defaultMessage='{totalReplies, plural, =0 {Reply} =1 {# reply} other {# replies}}'
                    values={{totalReplies}}
                />
            </span>

            {Boolean(lastReplyAt) && (
                <Timestamp
                    value={lastReplyAt}
                    {...THREADING_TIME}
                    updateIntervalInSeconds={60}
                >
                    {({formatted}) => (
                        <span className='Timestamp separated'>
                            <FormattedMessage
                                id='threading.footer.lastReplyAt'
                                defaultMessage='Last reply {formatted}'
                                values={{formatted}}
                            />
                        </span>
                    )}
                </Timestamp>
            )}

            <i className='fa fa-angle-right alt-visible' />
        </Button>
    );
};

const getThreadProps = (state: GlobalState, threadId: string) => {
    const post = getPost(state, threadId);
    const thread = selectThreadById(state, threadId);

    const isFollowing = getThreadPostFollowState(state, threadId);
    const totalReplies = getThreadReplyCount(state, threadId);

    return {
        totalReplies,
        unreadReplies: thread?.unread_replies || 0,
        lastReplyAt: thread?.last_reply_at || post?.last_reply_at || 0,
        isFollowing,
        channelId: post?.channel_id || thread?.channel_id,
    };
};

const getThreadParticipantsIds = (state: GlobalState, threadId: string) => {
    const post = getPost(state, threadId);
    if (!post) {
        return [];
    }

    const thread = selectThreadOrSyntheticThread(state, post);
    const participantIds = thread?.participants?.map(({id}) => id).reverse();

    return participantIds;
};

function ThreadFooter({threadId, replyClick}: Props) {
    const dispatch = useAppDispatch();
    const {onLogPostAction} = useContext(PostContext);
    const {unreadReplies, channelId, isFollowing, lastReplyAt, totalReplies} = useAppSelector(
        (state: GlobalState) => getThreadProps(state, threadId),
        isEqual,
    );

    const participantIds = useAppSelector((state: GlobalState) => getThreadParticipantsIds(state, threadId), isEqual);

    /**
     * @TODO: Перенести(?) получение треда на более высокий уровень
     * например, чтобы это проверялось при появлении поста в вьюпорте, а не футера поста
     */
    const {threadElementRef} = useFetchThread<HTMLDivElement>(threadId);

    const handleReply = (event: React.MouseEvent) => {
        event.stopPropagation();

        if (replyClick) {
            replyClick(event);
            return;
        }

        trackEvent('crt', 'replied_using_footer');
        markThreadLinkClicked({detail: {sourceType: 'link'}});
        onLogPostAction('replies', 'click');
        dispatch(selectPost({id: threadId, channel_id: channelId} as Post));
    };

    if (!totalReplies) {
        return null;
    }

    return (
        <div
            ref={threadElementRef}
            className='ThreadFooter'
        >
            {!isFollowing || !unreadReplies ? (
                <div className='indicator' />
            ) : (
                <NewReplies newReplies={unreadReplies} />
            )}

            {totalReplies > 0 && (
                <LastReplyButton
                    onClick={handleReply}
                    userIds={participantIds}
                    lastReplyAt={lastReplyAt}
                    totalReplies={totalReplies}
                />
            )}
        </div>
    );
}

export default memo(ThreadFooter);
